// Accounts Receivable view models for the admin frontend. Shapes mirror the CPHub.API
// `ARController` responses (ARAccount, ARAging, ARTransaction).

import moment from "moment";

// The statement period contract, shared by the single-statement generator and the monthly batch so
// the two cannot drift on defaults or on what counts as a valid range.
export const arDefaultPeriodStart = (): string =>
    moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");

export const arDefaultPeriodEnd = (): string =>
    moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD");

export const arDefaultDueByDate = (): string => moment().add(30, "days").format("YYYY-MM-DD");

/** A cleared date is not a range, and Period End may fall on the start day but never before it. */
export function arIsValidStatementPeriod(periodStart?: string | null, periodEnd?: string | null): boolean {
    if (!periodStart || !periodEnd) return false;
    return !moment(periodEnd).isBefore(moment(periodStart), "day");
}

export interface ARAccountPatientLink {
    id?: number;
    arAccountId?: number;
    patientId?: number;
    active?: boolean;
}

export interface ARAccountSummary {
    id: number;
    accountNumber?: string;
    responsiblePatientId?: number;
    status?: number;
    defaultCreditLimit?: number | null;
    currentBalance?: number;
    statementDeliveryMethods?: number | null;
    notes?: string | null;
    linkedPatients?: ARAccountPatientLink[] | null;
    autoPayEnabled?: boolean | null;
    autoPayStripeCardId?: string | null;
    autoPayMaxAmount?: number | null;
}

// Auto-pay enrollment state for an account, from GET /AR/accounts/{id}/autopay. `maxAmount` is the
// staff-configured ceiling on a single auto-pay charge, for patients on a negotiated payment
// arrangement; null means no ceiling and the full statement balance is charged.
export interface ARAutoPayStatus {
    enabled: boolean;
    stripeCardId: string | null;
    maxAmount: number | null;
}

// A saved Stripe card returned by GET /AR/accounts/{id}/payment-methods (the responsible
// patient's payment methods), used to pick a card when enrolling an account in auto-pay.
export interface ARSavedCard {
    id: string;
    brand?: string | null;
    cardNumber?: string | null;
    expMonth?: number | null;
    expYear?: number | null;
}

// The saved-card picker's labels, shared by every AR modal that offers one — the auto-pay and
// payment modals sit on the same dashboard, so the same card has to read the same way in both.
export function arSavedCardOptions(cards: ARSavedCard[]): { value: string | null; text: string }[] {
    const options = cards.map(c => ({
        value: c.id as string | null,
        text: `${c.brand ?? "Card"} •••• ${c.cardNumber ?? ""} (exp ${c.expMonth ?? "--"}/${c.expYear ?? "--"})`,
    }));
    return [{ value: null, text: "Select a card..." }, ...options];
}

// Statement delivery channels are bit flags on the account. Print/Mail (bit 1) is intentionally
// not offered — there is no mail-vendor integration; statements deliver via email, portal, and SMS.
export const ARDeliveryMethodOptions: { value: number; text: string }[] = [
    { value: 2, text: "Email" },
    { value: 4, text: "Patient Portal Only" },
    { value: 8, text: "SMS with Portal Link" },
];

export function arDeliveryFlagsToArray(flags?: number | null): number[] {
    if (!flags) return [];
    return ARDeliveryMethodOptions.filter(o => (flags & o.value) === o.value).map(o => o.value);
}

export function arDeliveryArrayToFlags(values: number[]): number {
    return values.reduce((acc, v) => acc | v, 0);
}

export interface ARAging {
    arAccountId?: number;
    agingCurrent?: number;
    agingBucket1?: number;
    agingBucket2?: number;
    agingBucket3?: number;
    agingBucket4Plus?: number;
    total?: number;
}

export interface ARPastDueAccount {
    arAccountId?: number;
    accountNumber?: string;
    responsiblePatientId?: number;
    status?: number;
    balance?: number;
    agingCurrent?: number;
    agingBucket1?: number;
    agingBucket2?: number;
    agingBucket3?: number;
    agingBucket4Plus?: number;
    pastDueTotal?: number;
}

export interface ARStatementSummary {
    id: number;
    arAccountId?: number;
    statementDate?: string;
    periodStart?: string;
    periodEnd?: string;
    previousBalance?: number;
    periodCharges?: number;
    periodCredits?: number;
    endingBalance?: number;
    dueByDate?: string;
    status?: number;
    // Delivery state, filled in by the render/archive/deliver step. A statement with no pdfPath
    // has not been rendered yet, so there is nothing to open or download.
    deliveryMethods?: number | null;
    pdfPath?: string | null;
    deliveredDate?: string | null;
}

// Result of POST /AR/statements/{id}/resend. `delivered` is the only thing that says the send
// reached a channel — deliveredDate can be an older send's, because UpdateDelivery preserves it.
export interface ARResendStatementResult {
    delivered: boolean;
    statement: ARStatementSummary;
}

// The one explanation of a statement that was archived but pushed to nobody. The Delivery Status
// modal shows it, and the Re-send toast that points the user at that modal repeats it, so the two
// have to come from the same place or they drift into contradicting each other.
export const arNoDeliveryChannelReason =
    "The responsible party may have no email address or mobile number on file.";

// Status is an enum on the statement (1 Generated, 2 Delivered) and is nullable in the model, so
// an unknown or absent status has to read as unknown rather than defaulting to Generated.
export function arStatementStatusLabel(status?: number | null): string {
    switch (status) {
        case 1: return "Generated";
        case 2: return "Delivered";
        default: return "Unknown";
    }
}

export function arStatementStatusVariant(status?: number | null): string {
    switch (status) {
        case 1: return "secondary"; // Generated
        case 2: return "success";   // Delivered
        default: return "light";
    }
}

// The delivery flags on a statement are the account's channels snapshotted at generation time.
// PrintMail (bit 1) is not offered when configuring an account but can still be present on
// historical rows, so it is labeled here even though ARDeliveryMethodOptions omits it.
const statementDeliveryLabels: { value: number; text: string }[] = [
    { value: 1, text: "Printed / Mailed" },
    ...ARDeliveryMethodOptions,
];

export function arStatementDeliveryMethodsLabel(flags?: number | null): string {
    if (!flags) return "None";
    const matched = statementDeliveryLabels.filter(o => (flags & o.value) === o.value).map(o => o.text);
    return matched.length ? matched.join(", ") : "None";
}

// Result of GET /AR/accounts/{id}/credit-check?amount=. Drives whether AR may be elected as a
// tender at checkout and whether an over-limit override is required.
export interface ARCreditCheck {
    arAccountId?: number;
    status?: number;
    creditLimit?: number | null;
    balance?: number;
    availableCredit?: number | null;
    projectedBalance?: number;
    overLimit: boolean;
    allowed: boolean;
}

export interface ARPaymentView {
    id: number;
    arAccountId?: number;
    arTransactionId?: number;
    amount?: number;
    paymentMethod?: number;
    checkNumber?: string | null;
    referenceId?: string | null;
    // Set only when CPHub charged the card itself. Its presence is what makes a void refund, so it
    // is what the void modal keys off to warn that money will be returned.
    stripeChargeId?: string | null;
    status?: number;
    reasonCode?: string | null;
    notes?: string | null;
    created?: string;
}

export const ARPaymentMethodOptions: { value: number; text: string }[] = [
    { value: 1, text: "Cash" },
    { value: 2, text: "Check" },
    { value: 3, text: "Credit Card" },
];

// Manual transaction types a user may post (system types are excluded).
export const ARAdjustmentTypeOptions: { value: number; text: string }[] = [
    { value: 4, text: "Manual Adjustment" },
    { value: 5, text: "Courtesy Credit" },
    { value: 7, text: "Balance Forward" },
    { value: 8, text: "Opening Balance" },
];

export function arPaymentMethodLabel(method?: number): string {
    return ARPaymentMethodOptions.find(o => o.value === method)?.text ?? "";
}

export function arPaymentStatusLabel(status?: number): string {
    return status === 2 ? "Voided" : status === 1 ? "Posted" : "";
}

export interface ARLedgerEntry {
    id: number;
    arAccountId?: number;
    orderId?: number | null;
    transactionType?: number;
    amount?: number;
    reasonCode?: string | null;
    isSystemGenerated?: boolean;
    notes?: string | null;
    created?: string;
}

const accountStatusLabels: Record<number, string> = {
    1: "Active",
    2: "On Hold",
    3: "Closed",
    4: "Written Off",
};

const transactionTypeLabels: Record<number, string> = {
    1: "Order Charge",
    2: "Insurance Adjustment",
    3: "Reversal Credit",
    4: "Manual Adjustment",
    5: "Courtesy Credit",
    6: "Write-Off",
    7: "Balance Forward",
    8: "Opening Balance",
    9: "Payment",
    10: "Refund",
};

export function arAccountStatusLabel(status?: number): string {
    return status ? accountStatusLabels[status] ?? "Unknown" : "";
}

const accountStatusVariants: Record<number, string> = {
    1: "success",   // Active
    2: "warning",   // On Hold
    3: "secondary", // Closed
    4: "danger",    // Written Off
};

export function arAccountStatusVariant(status?: number): string {
    return (status ? accountStatusVariants[status] : undefined) ?? "light";
}

export function arTransactionTypeLabel(type?: number): string {
    return type ? transactionTypeLabels[type] ?? "Unknown" : "";
}
